import {useEffect, useMemo} from "react";
import {ScrollView, TouchableOpacity} from "react-native";
import {Column, Columns, Stack, Text, View} from "@unibuddy/patron";
import {IconCheck} from "@unibuddy/icons";
import useAuth from "../Auth/hooks/useAuth";
import {useGetCommunitiesQuery} from "../types";
import ActivityIndicator from "../General/ActivityIndicator/ActivityIndicator";
import {QueryErrorHandler} from "../General/Errors/QueryErrorHandler";
import {boldFontStyles} from "../Styles/fontStyles";
import {useCommunity} from "../Community/CommunityProvider/CommunityProvider";
import {useModal} from "../General/ModalProvider/ModalProvider";
import {AmboInboxHeaderProps} from "./AmboInboxHeader";

type AmboInboxCommunitiesProps = {
    redirectToRoot: () => void;
} & AmboInboxHeaderProps;

export enum AmboInboxCommunitiesStrings {
    Title = "Your Communities",
    NoCommunities = "You are not part of any communities",
    NoCommunitiesDescription = "Please contact your university administrator to send you an invite",
    UniversalInboxOption = "All",
    UniversalInboxOptionLabel = "Select universal inbox",
}

export const AmboInboxCommunities = ({
    showUniversalInbox,
    setShowUniversalInbox,
    redirectToRoot,
}: AmboInboxCommunitiesProps) => {
    const {user} = useAuth();
    const {select, selectedCommunity} = useCommunity();
    const {dismissModal} = useModal();

    const {data, loading, error, refetch} = useGetCommunitiesQuery({
        variables: {
            userId: user?.id ?? "",
        },
    });
    const communities = data?.getUser?.communities;

    const commonFields = useMemo(
        () => ({
            archived: false,
            communityChatGroups: [],
            inviteLinks: [],
            requestedPublicGroups: 0,
        }),
        [],
    );

    useEffect(() => {
        if (!communities?.length) return;
        if (!!selectedCommunity) return;
        const firstCommunityToSelect = communities[0];
        select({
            ...firstCommunityToSelect,
            ...commonFields,
        });
    }, [communities, selectedCommunity, select, commonFields]);

    const onSelectCommunity = (communityId: string) => {
        setShowUniversalInbox(false);
        const selectedCommunity = communities?.find((community) => community.id === communityId);
        if (!selectedCommunity) return;
        select({
            ...selectedCommunity,
            ...commonFields,
        });
        dismissModal();
        redirectToRoot();
    };

    const onSelectAll = () => {
        setShowUniversalInbox(true);
        dismissModal();
    };

    if (loading) {
        return (
            <View paddingY="xxlarge">
                <ActivityIndicator accessibilityLabel="Loading communities ..." />
            </View>
        );
    }

    if (error) {
        return (
            <View paddingY="large">
                <QueryErrorHandler
                    error={error}
                    meta={{
                        component: "AmboInboxCommunities",
                        query: "useGetCommunitiesQuery",
                    }}
                    retryCallback={refetch}
                />
            </View>
        );
    }

    if (!communities || communities.length === 0) {
        return (
            <View paddingTop="xlarge" paddingBottom="medium" paddingX="medium">
                <Stack space="small-medium">
                    <Text
                        align="center"
                        style={{
                            ...boldFontStyles,
                        }}
                    >
                        {AmboInboxCommunitiesStrings.NoCommunities}
                    </Text>
                    <Text align="center">
                        {AmboInboxCommunitiesStrings.NoCommunitiesDescription}
                    </Text>
                </Stack>
            </View>
        );
    }

    return (
        <ScrollView>
            <View paddingY="xlarge">
                <Stack space="large">
                    <Text
                        align="center"
                        style={{
                            ...boldFontStyles,
                        }}
                    >
                        {AmboInboxCommunitiesStrings.Title}
                    </Text>
                    <Stack space="small">
                        <TouchableOpacity
                            accessibilityRole="button"
                            onPress={onSelectAll}
                            accessibilityLabel={
                                AmboInboxCommunitiesStrings.UniversalInboxOptionLabel
                            }
                        >
                            <View flexDirection="row" paddingY="small" paddingX="large">
                                <Columns verticalAlign="center">
                                    <Column>
                                        <Text>
                                            {AmboInboxCommunitiesStrings.UniversalInboxOption}
                                        </Text>
                                    </Column>
                                    {showUniversalInbox && (
                                        <Column width="content">
                                            <IconCheck width={18} height={18} />
                                        </Column>
                                    )}
                                </Columns>
                            </View>
                        </TouchableOpacity>
                        {communities?.map((community) => (
                            <TouchableOpacity
                                onPress={() => onSelectCommunity(community.id)}
                                key={community.id}
                                accessibilityLabel={`Select ${community.title}`}
                                accessibilityRole="button"
                            >
                                <View
                                    flexDirection="row"
                                    paddingY="small"
                                    paddingX="large"
                                    key={community.id}
                                >
                                    <Columns verticalAlign="center">
                                        <Column>
                                            <Text>{community.title}</Text>
                                        </Column>
                                        {selectedCommunity?.id === community.id &&
                                            !showUniversalInbox && (
                                                <Column width="content">
                                                    <IconCheck width={24} height={24} />
                                                </Column>
                                            )}
                                    </Columns>
                                </View>
                            </TouchableOpacity>
                        ))}
                    </Stack>
                </Stack>
            </View>
        </ScrollView>
    );
};
